import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { seo } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { useSession } from "@/hooks/use-session";
import { AVATARS, StudentAvatar } from "@/lib/avatars";
import { EXAMS } from "@/lib/exams";
import { saveProfilePreferences } from "@/lib/profile.functions";

export const Route = createFileRoute("/profile/edit")({
  head: () =>
    seo({
      title: "My Profile — The Quantum Minds",
      description: "Choose your avatar, class and target exam.",
      noindex: true,
    }),
  component: MyProfilePage,
});

const CLASSES = ["Class 9", "Class 10", "Class 11", "Class 12", "Dropper"];
const EXAM_OPTIONS = [...EXAMS.map((e) => e.shortName), "Other"];

const SELECT =
  "glass w-full appearance-none rounded-2xl bg-transparent px-4 py-3.5 text-sm text-foreground outline-none focus:border-primary/50";

function MyProfilePage() {
  const { profile, applyProfile } = useSession();
  const [avatarId, setAvatarId] = useState<string>(AVATARS[0].id);
  const [studyClass, setStudyClass] = useState("");
  const [targetExam, setTargetExam] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setAvatarId(profile.avatarId ?? AVATARS[0].id);
    setStudyClass(profile.studyClass ?? "");
    setTargetExam(profile.targetExam ?? "");
  }, [profile]);

  const save = async () => {
    setSaving(true);
    try {
      const next = await saveProfilePreferences({
        data: {
          avatarId,
          studyClass: studyClass || null,
          targetExam: targetExam || null,
        },
      });
      applyProfile(next);
      toast.success("Profile updated");
    } catch {
      toast.error("Could not save your profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-5 pb-32 pt-8">
      <div className="flex items-center gap-3">
        <Link
          to="/profile"
          aria-label="Back to profile"
          className="glass grid size-10 place-items-center rounded-full"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">My Profile</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Pick an avatar and keep your class and exam up to date.
          </p>
        </div>
      </div>

      <h2 className="mt-8 px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        Avatar
      </h2>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {AVATARS.map((a) => {
          const active = avatarId === a.id;
          return (
            <button
              key={a.id}
              type="button"
              aria-pressed={active}
              onClick={() => setAvatarId(a.id)}
              className={cn(
                "glass relative flex flex-col items-center gap-3 rounded-3xl p-4 transition active:scale-[0.98]",
                active
                  ? "border-primary/50 shadow-[var(--shadow-glow)]"
                  : "hover:border-border-strong",
              )}
            >
              <StudentAvatar id={a.id} className="size-16" />
              <span className="text-xs font-medium">{a.name}</span>
              {active ? (
                <span className="absolute right-3 top-3 grid size-6 place-items-center rounded-full bg-primary text-primary-foreground">
                  <Check className="size-3.5" />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <h2 className="mt-8 px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        Class
      </h2>
      <select
        aria-label="Class"
        className={cn(SELECT, "mt-3")}
        value={studyClass}
        onChange={(e) => setStudyClass(e.target.value)}
      >
        <option value="">Select your class</option>
        {CLASSES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>

      <h2 className="mt-8 px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        Target exam
      </h2>
      <select
        aria-label="Target exam"
        className={cn(SELECT, "mt-3")}
        value={targetExam}
        onChange={(e) => setTargetExam(e.target.value)}
      >
        <option value="">Select your exam</option>
        {EXAM_OPTIONS.map((e) => (
          <option key={e} value={e}>
            {e}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={save}
        disabled={saving || !profile}
        className="mt-8 flex w-full items-center justify-center gap-2 rounded-2xl bg-[image:var(--gradient-brand)] px-5 py-4 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition active:scale-[0.99] disabled:opacity-60"
      >
        {saving ? <Loader2 className="size-4 animate-spin" /> : null}
        Save Changes
      </button>
    </div>
  );
}
